import { useEffect, useRef, useState } from "react";
import Button from "app/storybookComponents/Button";
import Chatbot from "./Chatbot";
import Robot from "resources/images/robot.png";
import {
  selectAiFeaturesEnabled,
  selectHasCoachBoConversationHistoryEnabled,
} from "app/containers/AdminConsole/slice";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  getCoachBoConversations,
  getIntroMessageAndPreselectedPrompts,
  processConversation,
  resetChatbotState,
  selectChatbotConversationEntities,
  selectChatbotMessages,
  selectHidingChatbotButtonFromRoot,
  selectIsCoachbotOpen,
  selectIsThreadIdWeeklyCheckinMap,
  selectSampleChatState,
  selectShowFeedbackCardOnClose,
  selectSubmittingCoachbotFeedbackStatus,
  selectThreadId,
  setCoachBotPrompt,
  setIsCoachbotOpen,
  setSampleChatState,
  submitCoachbotFeedback,
  updateCoachbotConversation,
} from "./slice";
import TextArea from "app/storybookComponents/Forms/TextArea";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CHATBOT_FEEDBACK_EXAMPLES, CHATBOT_OTHER_FEEDBACK } from "./constants";
import { useLocation, useSearchParams } from "react-router-dom";
import { selectCurrentUserInfo } from "app/containers/Global/slice";
import {
  getTemplatePromptFromType,
  setAllianzGrowthMindsetCoachBoChat,
} from "../CoachBotPrompts/helpers";
import { onDismissWeeklyCheckInTodo, setSavedConversation } from "./helpers";
import { selectAllCoachBoCheckInNotifications } from "app/components/Notifications/slice";

export default function FloatingChatbotButton() {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryThreadId = searchParams.get("threadId");
  const customChatType = searchParams.get("customChatType");

  const isOpen = useAppSelector(selectIsCoachbotOpen);
  const areAiFeaturesEnabled = useAppSelector(selectAiFeaturesEnabled);
  const forceHideChatbotButton = useAppSelector(
    selectHidingChatbotButtonFromRoot
  );
  const threadId = useAppSelector(selectThreadId);
  const submittingFeedbackStatus = useAppSelector(
    selectSubmittingCoachbotFeedbackStatus
  );
  const savedChatBoConversationsEntities = useAppSelector(
    selectChatbotConversationEntities
  );
  const currentUserInfo = useAppSelector(selectCurrentUserInfo);
  const showFeedbackCardOnClose = useAppSelector(selectShowFeedbackCardOnClose);
  const chatBotMessages = useAppSelector(selectChatbotMessages);
  const isConversationHistoryEnabledCompanyWide = useAppSelector(
    selectHasCoachBoConversationHistoryEnabled
  );
  const isThreadIdWeeklyCheckinMap = useAppSelector(
    selectIsThreadIdWeeklyCheckinMap
  );
  const allCoachBoCheckInNotifications = useAppSelector(
    selectAllCoachBoCheckInNotifications
  );
  const sampleChatState = useAppSelector(selectSampleChatState);

  const [showFeedbackCard, setShowFeedbackCard] = useState(false);
  const [isHelpful, setIsHelpful] = useState<null | boolean>(null);
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackInputText, setFeedbackInputText] = useState("");
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const feedBackShowingRef = useRef<boolean | null>(null);

  useEffect(() => {
    setShowFeedbackCard(false);
    feedBackShowingRef.current = false;
  }, [pathname]);

  useEffect(() => {
    if (isOpen) {
      setShowFeedbackCard(false);
      feedBackShowingRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!customChatType || !currentUserInfo?.companyAccountId) {
      return;
    }

    setSearchParams((sp) => {
      sp.delete("customChatType");
      return sp;
    });

    const chatTemplate = getTemplatePromptFromType(
      customChatType,
      currentUserInfo?.companyAccountId
    );

    if (chatTemplate?.chatType === "allianzGrowthMindsetChat") {
      setAllianzGrowthMindsetCoachBoChat(
        dispatch,
        currentUserInfo?.companyAccountId
      );
    }

    // For now we are not setting the other chatType via query params but building out to support it
  }, [
    customChatType,
    dispatch,
    setSearchParams,
    currentUserInfo?.companyAccountId,
  ]);

  // Opens a saved conversation from a ?threadId= link (e.g. the coaching plan email).
  useEffect(() => {
    if (!queryThreadId) {
      return;
    }

    const selectedConversation =
      savedChatBoConversationsEntities[queryThreadId];

    if (!selectedConversation) {
      dispatch(getCoachBoConversations());
      return;
    }

    setSearchParams((sp) => {
      sp.delete("threadId");
      return sp;
    });

    setSavedConversation(dispatch, selectedConversation);
  }, [
    dispatch,
    queryThreadId,
    savedChatBoConversationsEntities,
    setSearchParams,
  ]);

  const closeFeedbackCard = () => {
    setShowFeedbackCard(false);
    setIsHelpful(null);
    setShowFeedbackInput(false);
    setFeedbackInputText("");
    dispatch(resetChatbotState());
    feedBackShowingRef.current = false;
  };

  // Function to show the feedback card and start the timer
  const onShowFeedbackCard = () => {
    setShowFeedbackCard(true);
    feedBackShowingRef.current = true;
    startTimer(5000);
    // Only process the conversation if the user has the setting enabled
    // and the feature flag is enabled
    if (
      threadId &&
      currentUserInfo?.hasCoachBoConversationHistoryEnabled &&
      isConversationHistoryEnabledCompanyWide
    ) {
      const isWeeklyCheckInChat =
        (threadId && isThreadIdWeeklyCheckinMap[threadId]) ||
        (sampleChatState.isSampleChatShowing &&
          sampleChatState.isCheckingSampleChat);
      dispatch(
        processConversation({
          threadId,
          isSampleChat: sampleChatState?.isSampleChatShowing,
          isWeeklyCheckInChat,
        })
      );
    }

    if (threadId && isThreadIdWeeklyCheckinMap[threadId]) {
      onDismissWeeklyCheckInTodo(
        dispatch,
        allCoachBoCheckInNotifications,
        threadId
      );
    }
  };

  // When starting the timer we clear any existing timer and set a new one
  const startTimer = (time: number = 3000) => {
    timerRef.current && clearTimeout(timerRef.current); // Clear any existing timer
    if (!timerRef) {
      return;
    }

    timerRef.current = setTimeout(() => {
      if (feedBackShowingRef.current) {
        closeFeedbackCard();
      }
    }, time);
  };

  const onSubmitFeedback = (feedbackString: string) => {
    // send request to the api
    if (isHelpful !== null) {
      dispatch(
        submitCoachbotFeedback({
          feedback: feedbackString,
          feedbackType: isHelpful ? "thumbsUp" : "thumbsDown",
        })
      );
    }
  };

  const getFeedbackExamples = () => {
    if (submittingFeedbackStatus === "succeeded") {
      return null;
    }

    if (showFeedbackInput) {
      return (
        <div>
          <TextArea
            inputLabel="Let us know your comments:"
            inputText={feedbackInputText}
            onTextChange={(e) => setFeedbackInputText(e)}
            placeholder="Feel free to add any specific details here..."
            controlId="feedback-input"
          />
          <Button
            onClick={() => {
              onSubmitFeedback(feedbackInputText);
            }}
            disabled={!feedbackInputText}
            style={{
              marginTop: "12px",
            }}
          >
            Submit
          </Button>
        </div>
      );
    }

    const helpfulOrNot = isHelpful ? "helpful" : "notHelpful";
    return (
      <div>
        <p className="textarea-label mb-2">Tell us more:</p>
        <div className="row-gap-8px flex-wrap">
          {CHATBOT_FEEDBACK_EXAMPLES[helpfulOrNot].map((tip) => (
            <Button
              onClick={() => {
                if (tip === CHATBOT_OTHER_FEEDBACK) {
                  setShowFeedbackInput(true);
                } else {
                  onSubmitFeedback(tip);
                }
              }}
              variant="secondary-blue"
              disabled={submittingFeedbackStatus === "loading"}
              key={tip}
            >
              {tip}
            </Button>
          ))}
        </div>
      </div>
    );
  };

  const getHelpfulButton = (helpful: boolean, active?: boolean) => (
    <Button
      onClick={() => {
        setIsHelpful(helpful);
      }}
      variant="secondary-blue"
      active={active}
    >
      <FontAwesomeIcon
        icon={`thumbs-${helpful ? "up" : "down"}`}
        className="me-2"
      />
      {helpful ? "" : "Not "}Helpful
    </Button>
  );

  const getFeedbackButtonText = () => {
    if (isHelpful === null) {
      return (
        <div className="column-gap-12px">
          <div>
            <p>How was your conversation with Coach Bo?</p>
            <p>Let us know so we can continue to improve your experience.</p>
          </div>
          <div className="row-gap-8px">
            <div>{getHelpfulButton(true)}</div>
            <div>{getHelpfulButton(false)}</div>
          </div>
        </div>
      );
    }

    return (
      <div className="row-gap-12px align-items-center">
        {getHelpfulButton(isHelpful, true)}
        <p>Thank you for your feedback!</p>
      </div>
    );
  };

  const getFeedbackButton = () => {
    if (!showFeedbackCard) {
      return null;
    }
    return (
      <div
        className="floating-chatbot-feedback-card"
        onMouseEnter={() => {
          timerRef?.current && clearTimeout(timerRef.current);
        }}
        onMouseLeave={() => {
          startTimer(3000);
        }}
      >
        <div className="row-gap-16px align-items-center">
          <FontAwesomeIcon
            icon={["far", "circle-question"]}
            fontSize={16}
            className={isHelpful === null ? "align-self-start" : ""}
          />
          {getFeedbackButtonText()}
          <div className="align-self-start ms-auto">
            <Button
              xIcon
              variant="secondary-blue"
              onClick={closeFeedbackCard}
              className="border-0  p-0"
            />
          </div>
        </div>
        {isHelpful === null ? null : <>{getFeedbackExamples()}</>}
      </div>
    );
  };

  const onOpenChatbot = async () => {
    dispatch(setIsCoachbotOpen(true));
    const { fullBotIntro, userPrompts, isStale } = await dispatch(
      getIntroMessageAndPreselectedPrompts({ type: "Default" })
    ).unwrap();
    if (isStale) return;
    dispatch(
      setCoachBotPrompt({
        userPrompts,
        fullBotIntro,
      })
    );
  };

  const onCloseChatbot = () => {
    dispatch(setIsCoachbotOpen(false));

    if (showFeedbackCardOnClose) {
      const existingConversation =
        (threadId && savedChatBoConversationsEntities[threadId]) ?? {};
      const now = new Date().toISOString();

      dispatch(
        updateCoachbotConversation({
          created_at: now,
          ...existingConversation, // If there is an existing conversation we should keep the created at the same
          updated_at: now,
          messages: chatBotMessages,
          threadId: threadId ?? "",
        })
      );
      return onShowFeedbackCard();
    }
    // if there is no threadId then we should reset the state
    dispatch(resetChatbotState());
    dispatch(setSampleChatState({ isSampleChatShowing: false }));
  };

  if (!areAiFeaturesEnabled) {
    return null;
  }

  return (
    <>
      <div className="analytics-sticky-footer" />
      <button
        className="floating-chatbot-button"
        style={{
          display: !isOpen && !forceHideChatbotButton ? "block" : "none",
        }}
        onClick={onOpenChatbot}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <img src={Robot} alt="Criteria" className="assistant-avatar" />
          <p>Need coaching advice?</p>
        </div>
      </button>
      <Chatbot isOpen={isOpen} closeChatbot={onCloseChatbot} />
      {getFeedbackButton()}
    </>
  );
}
